import type { PaginationDto, PaginationResponseDto } from "core/dto";
import { ORDER_BY } from "core/interfaces";
import type { PageMetaDto } from "shared/dto";

import { getOrderDefault } from "~/common/defaults/order.default";
import { getPaginationDefault } from "~/common/defaults/pagination.default";
import { getPaginationResponseDefault } from "~/common/defaults/pagination-response.default";
import { serializeOrderFilters } from "~/common/helpers/serialize-order-filters";
import { GetAllOrdersDto } from "~/common/interfaces/dto/orders/get-all-orders.dto";
import type { UpdateOrderByIdDto } from "~/common/interfaces/dto/orders/update-order-by-id.dto";
import type { Order } from "~/common/interfaces/entities/order.interface";
import type { Filter } from "~/common/interfaces/filter.interface";
import type { Sort } from "~/common/interfaces/sort.interface";

export const useOrdersStore = defineStore(
	"orders",
	() => {
		const currentOrder = ref<Order>(getOrderDefault());
		const orders = ref<Order[]>([]);
		const meta = ref<PageMetaDto>(getPaginationResponseDefault());
		const recentGetAllQuery = ref<GetAllOrdersDto & PaginationDto>();
		const tableFilter = ref<Filter[]>([
			{
				name: "Status",
				value: {
					init: false,
					opened: false,
					accepted: false,
					canceled: false,
					closed: false,
					fail: false
				}
			}
		]);
		const tableSort = ref<Sort>({
			columns: ["id", "createdAt", "status"],
			orderBy: ["id", ORDER_BY.DESC]
		});

		const getAll = async (
			dto: GetAllOrdersDto = {},
			paginationDto?: Partial<PaginationDto>
		): Promise<PaginationResponseDto<Order>> => {
			const filter = serializeOrderFilters(tableFilter.value);
			const [column, orderBy] = tableSort.value.orderBy;

			recentGetAllQuery.value = {
				...getPaginationDefault(),
				...paginationDto,
				...dto,
				...filter,
				column,
				orderBy
			};

			const res = await useNuxtApp().$apiFetch<PaginationResponseDto<Order>>(
				"/orders",
				{ query: recentGetAllQuery.value }
			);
			orders.value = res.data;
			meta.value = res.meta;

			return res;
		};
		const getById = (id: number): Promise<Order> => {
			return useNuxtApp().$apiFetch<Order>(`/orders/${id}`);
		};
		const patchById = (id: number, dto: UpdateOrderByIdDto): Promise<Order> => {
			return useNuxtApp().$apiFetch<Order>(`/orders/${id}`, {
				method: "PATCH",
				body: dto
			});
		};

		return {
			currentOrder,
			orders,
			meta,
			recentGetAllQuery,
			tableFilter,
			tableSort,

			getAll,
			getById,
			patchById
		};
	},
	{
		persist: {
			paths: ["tableFilter", "tableSort.orderBy"]
		}
	}
);
